/**
 * Utilidad para renderizar contenido del chatbot desde referencias
 * Permite cambio de idioma dinámico sin perder el flujo
 */

import chatContentMultilang from '../data/content.multilang.json';
import { I18n } from 'react-redux-i18n';

// Validate that the JSON data is loaded correctly
if (!chatContentMultilang || !chatContentMultilang.topics) {
  console.error(
    '❌ ERROR: chatbot-content.multilang.json not loaded or has invalid structure',
  );
  console.log('chatContentMultilang:', chatContentMultilang);
} else {
  console.log('✅ chatbot-content.multilang.json loaded successfully');
  console.log('Schema version:', chatContentMultilang.schemaVersion);
  console.log(
    'Available topics:',
    chatContentMultilang.topics.map((t) => t.key).join(', '),
  );
}

/**
 * Obtiene el texto correspondiente a una referencia en el idioma especificado
 * @param {Object} ref - Referencia del mensaje { step, section?, question?, answerIndex? }
 * @param {string} locale - Idioma: 'es', 'en', 'pt'
 * @returns {string} - Texto renderizado
 */
export const getContent = (ref, locale = 'es') => {
  const { step, section, question, answerIndex, optionLabel } = ref;

  try {
    // Manejo de textos estáticos usando i18n
    if (step === 'welcome') {
      const greeting = I18n.t('chatbot.welcome.greeting');
      const intro = I18n.t('chatbot.welcome.intro');
      const description = I18n.t('chatbot.welcome.description');
      const cta = I18n.t('chatbot.welcome.cta');
      return `${greeting}\n\n${intro}\n\n${description}\n\n${cta}`;
    }

    // Manejo de opciones especiales
    if (optionLabel) {
      if (typeof optionLabel === 'object' && optionLabel.key) {
        return I18n.t(`chatbot.${optionLabel.key}`) || optionLabel.key;
      }
      return optionLabel;
    }

    // Contenido desde el JSON - nueva estructura con topics
    if (!section) {
      return '';
    }

    // Buscar el topic por key
    const topic = chatContentMultilang.topics.find((t) => t.key === section);
    if (!topic) {
      console.warn(`Topic not found: ${section}`);
      return '';
    }

    // Menú de sección
    if (step === 'menu') {
      return topic.menu[locale] || topic.menu.es;
    }

    // Descripción de sección (intro)
    if (step === 'section') {
      return topic.intro[locale] || topic.intro.es;
    }

    // Pregunta
    if (step === 'question' && question) {
      const item = topic.items.find((i) => i.id === question);
      if (!item) {
        console.warn(`Question not found: ${question} in topic: ${section}`);
        return '';
      }
      return item.question[locale] || item.question.es;
    }

    // Respuesta
    if (step === 'answer' && question) {
      const item = topic.items.find((i) => i.id === question);
      if (!item) {
        console.warn(`Question not found: ${question} in topic: ${section}`);
        return '';
      }
      return item.answer[locale] || item.answer.es;
    }

    return '';
  } catch (error) {
    console.error('Error getting content:', error, ref);
    return '';
  }
};

/**
 * Obtiene todas las secciones para el menú principal
 * @param {string} locale - Idioma
 * @returns {Array} - Array de opciones de menú
 */
export const getMenuSections = (locale = 'es') => {
  try {
    if (!chatContentMultilang || !chatContentMultilang.topics) {
      console.error('chatContentMultilang or topics is missing');
      return [];
    }

    return chatContentMultilang.topics
      .filter((topic) => !topic.hideFromMenu)
      .map((topic) => ({
        id: topic.key,
        label: topic.menu[locale] || topic.menu.es,
        description: topic.intro[locale] || topic.intro.es,
      }));
  } catch (error) {
    console.error('Error in getMenuSections:', error);
    return [];
  }
};

/**
 * Obtiene todas las preguntas de una sección
 * @param {string} sectionId - ID de la sección (topic key)
 * @param {string} locale - Idioma
 * @returns {Array} - Array de preguntas
 */
export const getSectionQuestions = (sectionId, locale = 'es') => {
  try {
    if (!chatContentMultilang || !chatContentMultilang.topics) {
      console.error('chatContentMultilang or topics is missing');
      return [];
    }

    const topic = chatContentMultilang.topics.find((t) => t.key === sectionId);
    if (!topic) {
      console.warn(`Topic not found: ${sectionId}`);
      return [];
    }

    if (!topic.items || topic.items.length === 0) {
      console.warn(`No items found for topic: ${sectionId}`);
      return [];
    }

    return topic.items.map((item) => ({
      id: item.id,
      label: item.question[locale] || item.question.es,
    }));
  } catch (error) {
    console.error('Error in getSectionQuestions:', error, {
      sectionId,
      locale,
    });
    return [];
  }
};

/**
 * Verifica si una pregunta tiene respuestas
 * @param {string} sectionId - ID de la sección (topic key)
 * @param {string} questionId - ID de la pregunta (item id)
 * @returns {boolean}
 */
export const hasAnswers = (sectionId, questionId) => {
  try {
    const topic = chatContentMultilang.topics.find((t) => t.key === sectionId);
    if (!topic) return false;

    const item = topic.items.find((i) => i.id === questionId);
    if (!item) return false;

    return !!item.answer;
  } catch (error) {
    console.error('Error in hasAnswers:', error);
    return false;
  }
};

/**
 * Obtiene el contenido completo de una sección (para debugging)
 * @param {string} sectionId - ID de la sección (topic key)
 * @returns {Object}
 */
export const getSectionContent = (sectionId) => {
  try {
    return chatContentMultilang.topics.find((t) => t.key === sectionId) || null;
  } catch (error) {
    console.error('Error in getSectionContent:', error);
    return null;
  }
};

/**
 * Obtiene la configuración de descarga de un item, si tiene
 * @param {string} sectionId - ID de la sección (topic key)
 * @param {string} itemId - ID del item (pregunta)
 * @returns {Object|null} - { label: {es,en,pt}, files: {es,en,pt} } o null
 */
export const getItemDownloadConfig = (sectionId, itemId) => {
  try {
    const topic = chatContentMultilang.topics.find((t) => t.key === sectionId);
    if (!topic) return null;
    const item = topic.items.find((i) => i.id === itemId);
    return item?.downloadButton || null;
  } catch (error) {
    console.error('Error in getItemDownloadConfig:', error);
    return null;
  }
};

/**
 * Obtiene la configuración de navegación de un item, si tiene
 * @param {string} sectionId - ID de la sección (topic key)
 * @param {string} itemId - ID del item (pregunta)
 * @returns {Object|null} - { label: {es,en,pt}, section: string, question: string } o null
 */
export const getItemNavigationConfig = (sectionId, itemId) => {
  try {
    const topic = chatContentMultilang.topics.find((t) => t.key === sectionId);
    if (!topic) return null;
    const item = topic.items.find((i) => i.id === itemId);
    return item?.navigationButton || null;
  } catch (error) {
    console.error('Error in getItemNavigationConfig:', error);
    return null;
  }
};

/**
 * Resolves the back navigation target for a section.
 * Returns { type: 'section', section: parentSection } when the topic has a parentSection,
 * or { type: 'menu' } for top-level topics.
 * @param {string} sectionId - ID de la sección (topic key)
 * @returns {{ type: 'section', section: string } | { type: 'menu' }}
 */
export const resolveBackTarget = (sectionId) => {
  try {
    const topic = chatContentMultilang.topics.find((t) => t.key === sectionId);
    if (topic && topic.parentSection) {
      return { type: 'section', section: topic.parentSection };
    }
    return { type: 'menu' };
  } catch (error) {
    console.error('Error in resolveBackTarget:', error);
    return { type: 'menu' };
  }
};

/**
 * Obtiene metadata de un topic incluyendo requerimiento de rol
 * @param {string} sectionId - ID de la sección (topic key)
 * @returns {Object|null} - Metadata con requiresRole y deniedMessage, o null si no requiere rol
 */
export const getTopicMeta = (sectionId) => {
  const topic = chatContentMultilang.topics.find((t) => t.key === sectionId);
  if (!topic) return null;
  if (topic.requiresRole !== undefined) {
    return {
      requiresRole: topic.requiresRole,
      deniedMessage: topic.deniedMessage,
    };
  }
  return null;
};

/**
 * Obtiene la configuración de redirección de un topic.
 * `route` es la ruta de la app a la que se navega al elegir la sección en el menú.
 * `requiresRole` es el grupo necesario para esa ruta (undefined = ruta pública).
 * Es independiente de `requiresRole` a nivel topic, que gatea el CONTENIDO del chat.
 * @param {string} sectionId - ID de la sección (topic key)
 * @returns {{ route: string, requiresRole: number|undefined }|null}
 */
export const getTopicRoute = (sectionId) => {
  try {
    const topic = chatContentMultilang.topics.find((t) => t.key === sectionId);
    if (!topic || !topic.route) return null;
    return {
      route: topic.route,
      requiresRole: topic.routeRequiresRole,
    };
  } catch (error) {
    console.error('Error in getTopicRoute:', error);
    return null;
  }
};

/**
 * Obtiene la configuración de redirección de un item (pregunta), si tiene.
 * Mismo contrato que `getTopicRoute`, pero a nivel pregunta: permite que una
 * respuesta puntual lleve a una pantalla concreta (ej. alta de campaña).
 * @param {string} sectionId - ID de la sección (topic key)
 * @param {string} itemId - ID del item (pregunta)
 * @returns {{ route: string, requiresRole: number|undefined }|null}
 */
export const getItemRoute = (sectionId, itemId) => {
  try {
    const topic = chatContentMultilang.topics.find((t) => t.key === sectionId);
    if (!topic) return null;
    const item = topic.items.find((i) => i.id === itemId);
    if (!item || !item.route) return null;
    return {
      route: item.route,
      requiresRole: item.routeRequiresRole,
    };
  } catch (error) {
    console.error('Error in getItemRoute:', error);
    return null;
  }
};

/**
 * Resuelve el label de menú localizado de cualquier topic, incluidos los ocultos
 * (hideFromMenu). Permite que la burbuja del usuario al entrar a una sub-sección
 * (ej. Informes DISC / Flujo de navegación) se muestre traducida según el locale.
 * @param {string} sectionId - ID del topic
 * @param {string} locale - Idioma
 * @returns {string} - Label localizado, o el id si no se encuentra
 */
export const resolveMenuLabel = (sectionId, locale = 'es') => {
  try {
    const topic = chatContentMultilang.topics.find((t) => t.key === sectionId);
    if (!topic || !topic.menu) return sectionId;
    return topic.menu[locale] || topic.menu.es || sectionId;
  } catch (error) {
    console.error('Error in resolveMenuLabel:', error);
    return sectionId;
  }
};

export default {
  getContent,
  getMenuSections,
  getSectionQuestions,
  hasAnswers,
  getSectionContent,
  getItemDownloadConfig,
  getItemNavigationConfig,
  getTopicMeta,
  getTopicRoute,
  getItemRoute,
  resolveBackTarget,
  resolveMenuLabel,
};
